import '@/styles/secondary-surfaces.css';
import { lazy, Suspense, useEffect, useState, type ComponentType, type ReactNode } from "react";
import { Link, Navigate, Routes, Route, useLocation, useNavigate, useParams } from "react-router-dom";
import { MARKED_UP_LETTER_WORKFLOW_ROUTES } from "@/lib/markedUpLetterScreens";
import { DESIGN_PREVIEWS_ENABLED } from "@/lib/designPreviews";
import { capabilityForSlug, type RouteCapabilityDecision } from "@/lib/routeCapabilities";
import { checkSession } from "@/lib/api";
import { ErrorBoundary, PageSkeleton, SupportLink, SupportWidget } from "@/primitives";
import { browserReloadEnv, handleChunkLoadError } from "@/lib/chunkReload";
import { track } from "@/lib/analytics/client";
import { fillRouteParams, parkedRoutesEnabled, redactPathParams } from "@/lib/retiredRoutes";
// Legacy in-app bug-report bar.
import { ZoeFeedbackMount } from "@/feedback-toolbar/ZoeFeedbackMount";
import { useModeAwareDocumentTitle } from "@/lib/document-title";
import { RequireAuth, SessionCheckErrorPanel, sessionCheckError, type SessionCheckError } from './RequireAuth';
import { isAuthenticationFailure, isTransientFailure, SESSION_RETRY_DELAYS_MS } from '@/lib/session-failure';
import { OnboardingRetiredRedirect, WebsitePriceWallRedirect, inAppSignUpEnabled } from './journeyDoors';
import { useMe } from '@/lib/auth/useMe';

function lazyNamed<TProps = Record<string, never>>(loader: () => Promise<unknown>, exportName: string) {
  return lazy(async () => {
    try {
      const module = (await loader()) as Record<string, unknown>;
      return { default: module[exportName] as ComponentType<TProps> };
    } catch (error) {
      if (handleChunkLoadError(error, browserReloadEnv())) {
        await new Promise<never>(() => {});
      }
      throw error;
    }
  });
}
const LetterShell = lazyNamed(() => import("@/app/LetterShell"), "LetterShell");
const AskZoeLayout = lazyNamed(() => import("@/ask-zoe/routes"), "AskZoeLayout");
const AskZoeNewChat = lazyNamed(() => import("@/ask-zoe/routes"), "AskZoeNewChat");
const AskZoeThread = lazyNamed(() => import("@/ask-zoe/routes"), "AskZoeThread");
const AskZoeProjects = lazyNamed(() => import("@/ask-zoe/routes"), "AskZoeProjects");
const AskZoeProject = lazyNamed(() => import("@/ask-zoe/routes"), "AskZoeProject");
const AskZoeLibrary = lazyNamed(() => import("@/ask-zoe/routes"), "AskZoeLibrary");
const LetterReads = lazyNamed(() => import("@/screens/letter/Reads"), "LetterReads");
const LetterPortfolio = lazyNamed(() => import("@/screens/letter/Portfolio"), "LetterPortfolio");
const DeskInbox = lazyNamed(() => import('@/screens/letter/DeskWorkspacePages'), 'LetterDeskInbox');
const DeskFindings = lazyNamed(() => import('@/screens/letter/DeskWorkspacePages'), 'LetterDeskFindings');
const DeskDigest = lazyNamed(() => import('@/screens/letter/DeskWorkspacePages'), 'LetterDeskDigest');
const DeskCompanyMonitoring = lazyNamed(() => import('@/screens/letter/DeskCompanyMonitoring'), 'DeskCompanyMonitoring');
const DeskScore = lazyNamed(() => import('@/screens/letter/DeskScore'), 'DeskScore');
const DeskBenchmarks = lazyNamed(() => import('@/screens/letter/DeskBenchmarks'), 'DeskBenchmarks');
const LetterSurveys = lazyNamed(() => import("@/screens/letter/Surveys"), "LetterSurveys");
const LetterSettings = lazyNamed(() => import("@/screens/letter/Settings"), "LetterSettings");
const LetterPortco = lazyNamed(() => import("@/screens/letter/Portco"), "LetterPortco");
const LetterPortcoPulse = lazyNamed(() => import("@/screens/letter/PortcoPulse"), "LetterPortcoPulse");
const LetterSharedReport = lazyNamed(() => import("@/screens/letter/SharedReport"), "LetterSharedReport");
const LetterSignIn = lazyNamed(() => import("@/screens/letter/SignIn"), "LetterSignIn");
const OperatorWaitlist = lazyNamed(() => import("@/screens/letter/OperatorWaitlist"), "OperatorWaitlist");
const OperatorAccess = lazyNamed(() => import("@/screens/letter/OperatorWaitlist"), "OperatorAccess");
const AdminOperatorWaitlist = lazyNamed(() => import("@/screens/letter/AdminOperatorWaitlist"), "AdminOperatorWaitlist");
const LetterTwoFactorSetup = lazyNamed(() => import("@/screens/letter/TwoFactorSetup"), "LetterTwoFactorSetup");
const LetterMagicLink = lazyNamed(() => import("@/screens/letter/MagicLink"), "LetterMagicLink");
const OAuthConsent = lazyNamed(() => import("@/screens/letter/OAuthConsent"), "OAuthConsent");
const LetterInvite = lazyNamed(() => import("@/screens/letter/Invite"), "LetterInvite");
const DataAccessRequest = lazyNamed(() => import("@/screens/letter/DataAccessRequest"), "DataAccessRequest");
const LetterJoinWorkspace = lazyNamed(() => import("@/screens/letter/JoinWorkspace"), "LetterJoinWorkspace");
const LetterSurveyResponse = lazyNamed(() => import("@/screens/letter/SurveyResponse"), "LetterSurveyResponse");
const LetterPayReturn = lazyNamed(() => import("@/screens/letter/PayReturn"), "PayReturn");
const LetterPortfolioMatrix = lazyNamed(() => import("@/screens/letter/PortfolioViews"), "LetterPortfolioMatrix");
const LetterPortfolioQueue = lazyNamed(() => import("@/screens/letter/PortfolioViews"), "LetterPortfolioQueue");
const LetterLegal = lazyNamed(() => import("@/screens/letter/Legal"), "LetterLegal");
const ConnectAssistant = lazyNamed(() => import("@/screens/letter/ConnectAssistant"), "ConnectAssistant");
const LetterAdminDiagnostics = lazyNamed(() => import("@/screens/letter/AdminDiagnostics"), "LetterAdminDiagnostics");
const LetterAdminConfig = lazyNamed(() => import("@/screens/letter/AdminConfig"), "LetterAdminConfig");
const FounderFollowUpResponse = lazyNamed(() => import("@/screens/FounderFollowUpResponse"), "FounderFollowUpResponse");
const MarkedUpLetterIndex = lazyNamed(() => import("@/screens/MarkedUpLetterLibrary"), "MarkedUpLetterIndex");
const MarkedUpLetterRoute = lazyNamed<{ slug?: string }>(
  () => import("@/screens/MarkedUpLetterLibrary"),
  "MarkedUpLetterRoute",
);
const MulKit = lazyNamed(() => import("@/screens/MulKit"), "MulKit");
const CaseReadHistory = lazyNamed(() => import("@/screens/letter/CaseRead"), "CaseReadHistory");
const CaseRead = lazyNamed(() => import("@/screens/letter/CaseRead"), "CaseRead");
const CompanyOverview = lazyNamed(() => import("@/screens/letter/CompanyOverview"), "CompanyOverview");
const EvidenceDesk = lazyNamed(() => import("@/screens/letter/CompanyReadIntake"), "CompanyReadIntake");
const CompanySystems = lazyNamed(() => import("@/screens/letter/CompanySystems"), "CompanySystems");
const CompanyFiles = lazyNamed(() => import("@/screens/letter/CompanyFiles"), "CompanyFiles");
const CompanyComparison = lazyNamed(() => import("@/screens/letter/CompanyComparison"), "CompanyComparison");
const CompanyEvidence = lazyNamed(() => import('@/screens/letter/CompanyEvidence'), 'CompanyEvidence');
const CompanyInvestigations = lazyNamed(() => import('@/screens/letter/CompanyInvestigations'), 'CompanyInvestigations');
const CompanyDeck = lazyNamed(() => import("@/screens/letter/CompanyDeck"), "CompanyDeck");
const CompanyChat = lazyNamed(() => import("@/primitives/NativeAskZoe"), "NativeCompanyChat");
const IdealCaseClose = lazyNamed(() => import("@/screens/letter/ideal/CaseClose"), "IdealCaseClose");
const CaseOpen = lazyNamed(() => import("@/screens/letter/ideal/CaseOpen"), "CaseOpen");
const OnboardingContext = lazyNamed(() => import('@/screens/letter/onboarding/CompanyContext'), 'LetterOnboardingContext');

function NewReadEntry() {
  const identity = useMe();
  if (identity.state === 'loading') return <p role="status">Confirming your workspace…</p>;
  if (!identity.me) return <p role="alert">Your workspace could not be loaded. Refresh to try again.</p>;
  if (!identity.me.workspace?.onboarding_completed_at) return <Navigate to="/onboarding/welcome?next=%2Fdeal%2Fnew" replace />;
  // Journey amendment 2026-09-14 one setup page for every mode.
  return <CaseOpen />;
}

// Same rule as RequireAuth (ZPM-316): only a definitive "not signed in" — the
// probe answering `authed: false`, or a 401 — sends the visitor to /start. A
// transport failure, a timeout or a 5xx says nothing about who they are, so it
// is retried behind the blank loading state and then shown with a Try again,
// never read as "signed out".
export function HomeRoute() {
  const [target, setTarget] = useState<"loading" | "reads" | "start">("loading");
  const [checkError, setCheckError] = useState<SessionCheckError | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const run = (attempt: number) => {
      checkSession()
        .then((session) => {
          if (cancelled) return;
          setCheckError(null);
          setTarget(session.authed ? "reads" : "start");
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          if (isAuthenticationFailure(error)) {
            setTarget("start");
            return;
          }
          if (isTransientFailure(error) && attempt < SESSION_RETRY_DELAYS_MS.length) {
            retryTimer = setTimeout(() => run(attempt + 1), SESSION_RETRY_DELAYS_MS[attempt]);
            return;
          }
          const failure = sessionCheckError(error);
          // "Go to the home page" from the home page goes nowhere; asking again is the way forward.
          setCheckError(failure.recovery === "home" ? { ...failure, recovery: "retry" } : failure);
        });
    };
    setCheckError(null);
    run(0);
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
    };
  }, [retryNonce]);

  const errored = checkError !== null && checkError.recovery === "retry";
  useEffect(() => {
    if (!errored) return;
    const onOnline = () => setRetryNonce((n) => n + 1);
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [errored]);

  if (target === "loading") {
    if (checkError === null) return null;
    return (
      <SessionCheckErrorPanel
        error={checkError}
        onRetry={() => setRetryNonce((n) => n + 1)}
        onSignIn={() => navigate("/signin")}
        onHome={() => setRetryNonce((n) => n + 1)}
      />
    );
  }
  return <Navigate to={target === "reads" ? "/reads" : "/start"} replace />;
}

function V2Screen({ children }: { children: ReactNode }) {
  return <div data-v2>{children}</div>;
}

function CompatibilityRouteNotice({ children, label, target, capability }: { children: ReactNode; label: string; target?: string; capability?: RouteCapabilityDecision }) {
  return (
    <>
      <div
        role="status"
        data-route-compatibility-notice
        data-capability-id={capability?.capabilityId}
        data-release-status={capability?.status}
        style={{
          position: "fixed",
          left: 12,
          bottom: 12,
          zIndex: 9999,
          maxWidth: 520,
          border: "1px solid rgba(146, 64, 14, .35)",
          borderRadius: 10,
          background: "rgba(255, 251, 235, .96)",
          color: "#713f12",
          padding: "8px 10px",
          font: "11px/1.35 JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, monospace",
          letterSpacing: ".04em",
          boxShadow: "0 10px 30px rgba(17, 24, 39, .16)",
        }}
      >
        <strong>{label}</strong>
        {target ? <span> · compatibility route for {target}</span> : null}
        {capability ? <span> · {capability.reason}</span> : null}
      </div>
      {children}
    </>
  );
}

function DesignStubRoute({ children, slug }: { children: ReactNode; slug: string }) {
  const capability = capabilityForSlug(slug);
  return (
    <CompatibilityRouteNotice
      label={capability?.label ?? "DESIGN PREVIEW · STATIC STUB"}
      target={`${slug}; scores/statuses/copy are hardcoded and are not live backend data`}
      capability={capability}
    >
      {children}
    </CompatibilityRouteNotice>
  );
}

function LegacyDealSourcesRedirect() {
  // Preserve source bookmarks as direct routes to company-controlled access.
  const { codename = "" } = useParams<{ codename: string }>();
  return <Navigate to={`/deal/${encodeURIComponent(codename)}/access`} replace />;
}

function OnboardingConnectionsRedirect() {
  const identity = useMe();
  if (identity.state === 'loading') return <p role="status">Confirming your workspace…</p>;
  return <Navigate to={identity.me?.workspace?.mode === 'operator' ? '/settings?tab=company-systems&setup=onboarding' : '/deal/new'} replace />;
}

function LegacyDealSetupRedirect() {
  const { codename = "" } = useParams<{ codename: string }>();
  return <Navigate to={`/deal/${encodeURIComponent(codename)}`} replace />;
}

// Login is an alias of /signin; keep ?next= and friends so a deep link still lands.
function LoginAliasRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/signin", search: location.search }} replace />;
}

// X1 route cleanup — a MERGE row. Permanent redirect; each hit is counted so a route can be
// deleted by PR once it has had 30 days of zero hits. Patterns only: params are redacted.
export function RetiredRouteRedirect({ to }: { to: string }) {
  const params = useParams();
  const location = useLocation();
  const fromPattern = redactPathParams(location.pathname, params as Record<string, string | undefined>);
  useEffect(() => {
    track("route.retired_redirect", { from_pattern: fromPattern, to_pattern: to });
  }, [fromPattern, to]);
  const [pathname, search] = fillRouteParams(to, params as Record<string, string | undefined>).split("?");
  return <Navigate to={{ pathname, search: search ? `?${search}` : location.search }} replace />;
}

// Read bookmarks are durable case links, not time-limited retired routes.
function LegacyCaseReadRedirect() {
  const params = useParams();
  const location = useLocation();
  return <Navigate to={{ pathname: `/deal/${encodeURIComponent(params.codename ?? '')}`, search: location.search, hash: location.hash }} replace />;
}

// X1 route cleanup — an OUT-OF-SCOPE (parked) row.
export function ParkedRouteRedirect({ to, children }: { to: string; children: ReactNode }) {
  if (parkedRoutesEnabled()) return <>{children}</>;
  return <RetiredRouteRedirect to={to} />;
}

function RouteLoadingFallback() {
  return (
    <div data-route-loading>
      <PageSkeleton label="Loading Zoe…" />
    </div>
  );
}

function RouteErrorFallback() {
  const location = useLocation();
  return (
    <div role="alert" data-route-error-fallback style={{ padding: 32 }}>
      <h1 style={{ margin: 0, font: "600 18px/1.2 Instrument Sans, system-ui, sans-serif" }}>
        This Zoe view hit a snag.
      </h1>
      <p style={{ maxWidth: 560, color: "#57534e" }}>
        The rest of the app is still available. Refresh this route or use the navigation to continue.
      </p>
      <SupportLink subject="Zoe view error" context={{ route: location.pathname }} />
    </div>
  );
}

export function RouteResilience({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={`${location.pathname}${location.search}`} fallback={<RouteErrorFallback />}>
      <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

function DeskNotFound() {
  return <main className="desk-not-found">
    <Link to="/reads" className="desk-not-found__brand" aria-label="Zoe home">Zoe<span>.</span></Link>
    <section className="desk-not-found__body">
      <p className="desk-eyebrow">PAGE NOT FOUND · 404</p>
      <h1>This page isn’t here.</h1>
      <p>The link may have changed. Your companies and saved Reads are still available from Home.</p>
      <nav className="desk-not-found__destinations" aria-label="Continue in Zoe"><Link to="/reads"><strong>Open Home <span aria-hidden="true">→</span></strong><span>Return to your companies and saved Reads.</span></Link><Link to="/settings"><strong>Workspace settings <span aria-hidden="true">→</span></strong><span>Manage your profile, team and connections.</span></Link></nav>
    </section>
  </main>;
}

function DeskRouteAlias({ to }: { to: string }) {
  const location = useLocation();
  if (to.includes('?')) {
    const [pathname, defaults] = to.split('?');
    const search = new URLSearchParams(location.search);
    new URLSearchParams(defaults).forEach((value, key) => search.set(key, value));
    return <Navigate to={{ pathname, search: `?${search.toString()}`, hash: location.hash }} replace />;
  }
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

const IDEAL_SCREEN_OVERRIDE_ELEMENTS: Record<string, () => ReactNode> = {
  "deal-project-bloom-setup": () => <NewReadEntry />,
};

export function App() {
  useModeAwareDocumentTitle();
  return (
    <div className="app-root">
      <ZoeFeedbackMount />
      <div className="app-content-under-debug-bar">
        <RouteResilience>
          <Routes>
          <Route path="/" element={<HomeRoute />} />
          {/* Journey amendment 2026-09-14 the price wall is the website. No unpaid app door. */}
          <Route path="/start" element={<WebsitePriceWallRedirect />} />
          <Route path="/pay" element={<WebsitePriceWallRedirect />} />
          <Route path="/pay/return" element={<LetterPayReturn />} />
          {/* Public token pages (no Shell). /invite/:token folded into /join/:token (X1 row 72). */}
          <Route path="/join/:token" element={<LetterJoinWorkspace />} />
          <Route path="/share/:token" element={<LetterSharedReport />} />
          <Route path="/survey/:token" element={<ParkedRouteRedirect to="/start"><LetterSurveyResponse /></ParkedRouteRedirect>} />
          <Route path="/founder-followup/:token" element={<V2Screen><FounderFollowUpResponse /></V2Screen>} />
          {/* Auth screens use their own full-screen layout, no Shell. Email + magic link only. */}
          <Route path="/signin" element={<LetterSignIn />} />
          <Route path="/sign-up" element={inAppSignUpEnabled() ? <LetterSignIn /> : <WebsitePriceWallRedirect />} />
          <Route path="/login" element={<LoginAliasRedirect />} />
          <Route path="/magic-link" element={<LetterMagicLink />} />
          <Route path="/legal" element={<LetterLegal />} />
          <Route path="/legal/:page" element={<LetterLegal />} />
          {/* Public: set up an assistant to use Zoe (copy-paste install prompt; steps in /agents/install.md). */}
          <Route path="/connect" element={<ConnectAssistant />} />
          <Route element={<RequireAuth />}>
            {/* An assistant connecting over OAuth (routes/oauth-server.ts). Signed in, no app shell. */}
            <Route path="/oauth/consent" element={<OAuthConsent />} />
            {/* Journey amendment 2026-09-14 onboarding pages retired; setup completes silently. */}
            <Route path="/onboarding" element={<OnboardingRetiredRedirect />} />
            <Route path="/onboarding/welcome" element={<OnboardingRetiredRedirect />} />
            <Route path="/onboarding/mode" element={<OnboardingRetiredRedirect />} />
            <Route path="/onboarding/oath" element={<OnboardingRetiredRedirect />} />
            <Route path="/onboarding/intent" element={<OnboardingRetiredRedirect />} />
            <Route path="/onboarding/context" element={<OnboardingContext />} />
            <Route path="/onboarding/how-it-works" element={<OnboardingRetiredRedirect />} />
            <Route path="/onboarding/trust" element={<OnboardingRetiredRedirect />} />
            <Route path="/onboarding/connect" element={<OnboardingConnectionsRedirect />} />
            <Route path="/onboarding/threshold" element={<CaseOpen />} />
          </Route>
          {DESIGN_PREVIEWS_ENABLED && (
            <Route path="/marked-up-letter" element={<MarkedUpLetterIndex />} />
          )}
          {DESIGN_PREVIEWS_ENABLED && (
            <Route path="/marked-up-letter/:slug" element={<MarkedUpLetterRoute />} />
          )}
          {/* Marked-Up Letter design system — primitive verification gallery, dev/test only */}
          {DESIGN_PREVIEWS_ENABLED && (
            <Route path="/mul-kit" element={<MulKit />} />
          )}
          {/* Workflow screens still in design. The live new-read entry is routed explicitly inside the shell below. */}
          <Route element={<RequireAuth />}>
          {MARKED_UP_LETTER_WORKFLOW_ROUTES.map((route) => {
            if (route.slug === 'deal-project-bloom-setup') return null;
            const Override = IDEAL_SCREEN_OVERRIDE_ELEMENTS[route.slug];
            return (
              <Route
                key={route.path}
                path={route.path}
                element={<DesignStubRoute slug={route.slug}>{Override ? Override() : <MarkedUpLetterRoute slug={route.slug} />}</DesignStubRoute>}
              />
            );
          })}
          </Route>
          {/* X1 route cleanup (wayfinder tickets/04-deletion-list.md, approved 2026-09-03). */}
          <Route path="/demo" element={<RetiredRouteRedirect to="/start" />} />
          <Route path="/paywall" element={<RetiredRouteRedirect to="/pay" />} />
          <Route path="/security/two-factor" element={<LetterTwoFactorSetup />} />
          <Route path="/operator-waitlist" element={<OperatorWaitlist />} />
          <Route path="/operator-access" element={<OperatorAccess />} />
          <Route path="/invite/:token" element={<LetterInvite />} />
          <Route path="/deal/:codename/codename" element={<RetiredRouteRedirect to="/deal/new" />} />
          <Route path="/deal/:codename/stage" element={<RetiredRouteRedirect to="/deal/new" />} />
          <Route path="/deal/:codename/stage-asks" element={<LegacyDealSourcesRedirect />} />
          <Route path="/deal/:codename/claims/add" element={<RetiredRouteRedirect to="/deal/:codename/desk" />} />
          <Route path="/deal/:codename/founder-questions" element={<RetiredRouteRedirect to="/deal/:codename/desk" />} />
          <Route path="/deal/:codename/transcript" element={<RetiredRouteRedirect to="/deal/:codename/desk" />} />
          <Route path="/deal/:codename/integrations" element={<LegacyDealSourcesRedirect />} />
          <Route path="/deal/:codename/confidence" element={<RetiredRouteRedirect to="/deal/:codename/desk" />} />
          <Route path="/deal/:codename/invite-team" element={<LegacyDealSourcesRedirect />} />
          <Route path="/deal/:codename/declined" element={<RetiredRouteRedirect to="/deal/:codename/desk" />} />
          <Route path="/deal/:codename/data-syncing" element={<RetiredRouteRedirect to="/deal/:codename" />} />
          <Route path="/deal/:codename/running" element={<LegacyCaseReadRedirect />} />
          <Route path="/deal/:codename/first-findings" element={<RetiredRouteRedirect to="/deal/:codename" />} />
          <Route path="/deal/:codename/report" element={<LegacyCaseReadRedirect />} />
          <Route path="/deal/:codename/full" element={<RetiredRouteRedirect to="/deal/:codename" />} />
          <Route path="/deal/:codename/score-drilldown" element={<RetiredRouteRedirect to="/deal/:codename" />} />
          <Route path="/deal/:codename/recommendations" element={<RetiredRouteRedirect to="/deal/:codename" />} />
          <Route path="/deal/:codename/ask" element={<RetiredRouteRedirect to="/deal/:codename" />} />
          <Route path="/deal/:codename/take-out" element={<RetiredRouteRedirect to="/deal/:codename" />} />
          <Route path="/deal/:codename/ic-memo" element={<RetiredRouteRedirect to="/deal/:codename" />} />
          <Route path="/sources/:id" element={<RetiredRouteRedirect to="/settings?tab=sources" />} />
          <Route path="/scan/reading" element={<Navigate to="/start" replace />} />
          <Route path="/scan/reveal" element={<Navigate to="/start" replace />} />
          <Route path="/scan/teaser" element={<Navigate to="/start" replace />} />
          <Route path="/scan/paywall" element={<Navigate to="/pay" replace />} />
          <Route path="/scan/thin" element={<Navigate to="/start" replace />} />
          <Route path="/start/mode" element={<Navigate to="/signin" replace />} />
          <Route path="/welcome" element={<Navigate to="/start" replace />} />
          <Route path="/onboarding/desk" element={<Navigate to="/reads" replace />} />
          <Route path="/onboarding/operator" element={<Navigate to="/start" replace />} />
          <Route path="/desk" element={<Navigate to="/reads" replace />} />
          <Route path="/systems" element={<Navigate to="/settings?tab=company-systems" replace />} />
          <Route path="/debug/issues" element={<Navigate to="/admin/diagnostics" replace />} />
          <Route path="/deal/:codename/sources" element={<LegacyDealSourcesRedirect />} />
          <Route element={<LetterShell />}>
            {/* P10 — /reads is the only home. /today and /deals fold into it (X1 rows 56, 59). */}
            <Route path="/reads" element={<LetterReads />} />
            <Route path="/home" element={<DeskRouteAlias to="/reads" />} />
            <Route path="/inbox" element={<DeskInbox />} />
            <Route path="/alerts" element={<DeskInbox />} />
            <Route path="/findings" element={<DeskFindings />} />
            <Route path="/digest" element={<DeskDigest />} />
            <Route path="/digest/sunday" element={<DeskRouteAlias to="/digest" />} />
            {/* The live new-read entry sits in the shell so it shares the reads home's account menu. */}
            <Route path="/deal/new" element={<NewReadEntry />} />
            <Route path="/today" element={<Navigate to="/reads" replace />} />
            <Route path="/deals" element={<Navigate to="/reads" replace />} />
            <Route path="/settings" element={<LetterSettings />} />
            <Route path="/settings/audit" element={<Navigate to="/settings?tab=audit-log" replace />} />
            <Route path="/settings/audit-log" element={<Navigate to="/settings?tab=audit-log" replace />} />
            {/* STRIPE_PORTAL_RETURN_URL points here. */}
            <Route path="/settings/billing" element={<Navigate to="/settings?tab=billing" replace />} />
            {/* The MCP consent screen, agents/install.md and the MCP 401 copy all send people to "Settings, Integrations". */}
            <Route path="/settings/integrations" element={<Navigate to="/settings?tab=integrations" replace />} />
            <Route path="/settings/workspace" element={<DeskRouteAlias to="/settings?tab=workspace" />} />
            <Route path="/settings/team" element={<DeskRouteAlias to="/settings?tab=team" />} />
            <Route path="/settings/notifications" element={<DeskRouteAlias to="/settings?tab=notifications" />} />
            <Route path="/settings/security" element={<DeskRouteAlias to="/settings?tab=security" />} />
            <Route path="/settings/api" element={<DeskRouteAlias to="/settings?tab=api" />} />
            {/* P4 evidence desk, P5/P7 read page, P8 close. */}
            <Route path="/deal/:codename/desk" element={<EvidenceDesk />} />
            <Route path="/deal/:codename/systems" element={<CompanySystems />} />
            <Route path="/deal/:codename/files" element={<CompanyFiles />} />
            <Route path="/deal/:codename/data-room" element={<CompanyFiles />} />
            <Route path="/deal/:codename/claims" element={<CompanyComparison />} />
            <Route path="/deal/:codename/claim-check" element={<CompanyComparison />} />
            <Route path="/deal/:codename/score" element={<DeskScore />} />
            <Route path="/deal/:codename/benchmarks" element={<DeskBenchmarks />} />
            <Route path="/compare-companies" element={<CompanyComparison />} />
            <Route path="/deal/:codename/overview" element={<CompanyOverview />} />
            <Route path="/deal/:codename/evidence" element={<CompanyEvidence />} />
            <Route path="/deal/:codename/investigations" element={<CompanyInvestigations />} />
            <Route path="/deal/:codename/deck" element={<CompanyDeck />} />
            <Route path="/deal/:codename/documents" element={<CompanyDeck />} />
            <Route path="/deal/:codename/monitoring" element={<DeskCompanyMonitoring />} />
            <Route path="/deal/:codename/chat" element={<CompanyChat />} />
            <Route path="/deal/:codename/access" element={<DataAccessRequest />} />
            <Route path="/deal/:codename/close" element={<IdealCaseClose />} />
            <Route path="/deal/:codename" element={<CaseRead />} />
            <Route path="/deal/:codename/history" element={<CaseReadHistory />} />
            <Route path="/deal/:codename/setup" element={<LegacyDealSetupRedirect />} />
            <Route path="/deal/:codename/setup/advanced" element={<LegacyDealSetupRedirect />} />
            <Route path="/admin/config" element={<LetterAdminConfig />} />
            <Route path="/admin/operator-waitlist" element={<AdminOperatorWaitlist />} />
            <Route path="/admin/diagnostics" element={<LetterAdminDiagnostics />} />
            <Route path="/portfolio" element={<LetterPortfolio />} />
            <Route path="/portfolio/matrix" element={<LetterPortfolioMatrix />} />
            <Route path="/portfolio/queue" element={<LetterPortfolioQueue />} />
            <Route path="/compare" element={<DeskRouteAlias to="/compare-companies" />} />
            <Route path="/surveys" element={<ParkedRouteRedirect to="/reads"><LetterSurveys /></ParkedRouteRedirect>} />
            <Route path="/portco/:slug" element={<LetterPortco />} />
            <Route path="/portco/:slug/pulse" element={<LetterPortcoPulse />} />
          </Route>
          <Route path="/today/stub" element={<Navigate to="/reads" replace />} />
          <Route path="/settings/sources" element={<Navigate to="/settings?tab=sources" replace />} />
          {/* As on main: a signed-out visitor to an unknown address signs in first (and returns to it); a signed-in one sees not found. */}
          <Route element={<RequireAuth />}>
            <Route path="/ask" element={<AskZoeLayout />}>
              <Route index element={<Navigate to="/ask/chat" replace />} />
              <Route path="/ask/chat" element={<AskZoeNewChat />} />
              <Route path="/ask/chat/:threadId" element={<AskZoeThread />} />
              <Route path="/ask/projects" element={<AskZoeProjects />} />
              <Route path="/ask/projects/:projectId" element={<AskZoeProject />} />
              <Route path="/ask/library" element={<AskZoeLibrary />} />
            </Route>
          </Route>
          <Route element={<RequireAuth />}>
            <Route path="*" element={<DeskNotFound />} />
          </Route>
          </Routes>
        </RouteResilience>
        <ErrorBoundary fallback={null}>
          <SupportWidget />
        </ErrorBoundary>
      </div>
    </div>
  );
}
